/**
 * ScrollArea Component
 *
 * Professional custom scrollbar using OverlayScrollbars library.
 * Provides consistent cross-browser scrollbars with overlay positioning.
 *
 * @example
 * ```tsx
 * <ScrollArea variant="thin" color="primary" className="h-96">
 *   <YourContent />
 * </ScrollArea>
 * ```
 */

'use client';

import React from 'react';
import {
  OverlayScrollbarsComponent,
  type OverlayScrollbarsComponentRef,
} from 'overlayscrollbars-react';
import type { OverlayScrollbarsComponentProps } from 'overlayscrollbars-react';
import { cn } from '@/lib/utils';

export interface ScrollAreaProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'children'> {
  /**
   * Scrollbar width variant
   * @default 'default'
   */
  variant?: 'thin' | 'default' | 'thick' | 'none';

  /**
   * Scrollbar color theme
   * @default 'default'
   */
  color?: 'default' | 'primary' | 'accent';

  /**
   * Show scrollbar only on container hover
   * @default true
   */
  autoHide?: boolean;

  /**
   * Children to render inside scroll area
   */
  children: React.ReactNode;

  /**
   * Additional CSS classes
   */
  className?: string;
}

/**
 * ScrollArea - Professional custom scrollbar
 */
export const ScrollArea = React.forwardRef<OverlayScrollbarsComponentRef<'div'>, ScrollAreaProps>(
  (
    {
      variant = 'thin',
      color = 'default',
      autoHide = true,
      children,
      className,
      ...props
    },
    ref
  ) => {
    const containerClasses = cn(
      'os-theme-harvesthouse',
      variant === 'thin' && 'os-size-thin',
      variant === 'thick' && 'os-size-thick',
      color === 'primary' && 'os-color-primary',
      color === 'accent' && 'os-color-accent',
      className
    );

    const options: OverlayScrollbarsComponentProps['options'] = {
      scrollbars: {
        autoHide: autoHide ? 'move' : 'never',
        autoHideDelay: 800,
        theme: 'os-theme-harvesthouse',
      },
      overflow: {
        x: 'hidden',
        y: 'scroll',
      },
    };

    return (
      <OverlayScrollbarsComponent
        ref={ref}
        className={containerClasses}
        options={options}
        defer
        {...props}
      >
        {children}
      </OverlayScrollbarsComponent>
    );
  }
);

ScrollArea.displayName = 'ScrollArea';
